/**
 * Lending Calculations Library
 * Handles late fees, interest calculations, and loan amortization
 */

export interface LateFeeConfig {
    gracePeriodDays: number;
    flatFee: number;
    percentageRate: number;
    maxPercentageFee: number;
    compoundDaily: boolean;
    currency: string;
}

export interface LateFeeResult {
    isLate: boolean;
    daysLate: number;
    flatFee: number;
    percentageFee: number;
    totalFee: number;
    totalAmountDue: number;
    currency: string;
    breakdown: {
        originalAmount: number;
        lateFee: number;
        interestAccrued: number;
    };
}

export interface InstallmentInfo {
    installmentNumber: number;
    dueDate: Date;
    principalDue: number;
    interestDue: number;
    totalDue: number;
    amountPaid: number;
    status: "pending" | "paid" | "partial" | "late" | "overdue";
    lateFee: number;
    remainingBalance: number;
}

export interface LoanSummary {
    principal: number;
    totalInterest: number;
    totalAmount: number;
    monthlyPayment: number;
    effectiveApr: number;
    currency: string;
    termMonths: number;
    installments: InstallmentInfo[];
}

// Default late fee configuration (can be overridden by system settings)
export const DEFAULT_LATE_FEE_CONFIG: LateFeeConfig = {
    gracePeriodDays: 3, // 3 days grace period after due date
    flatFee: 50, // K50 flat fee
    percentageRate: 0.5, // 0.5% per day
    maxPercentageFee: 10, // Maximum 10% of principal
    compoundDaily: false,
    currency: "ZMW",
};

/**
 * Calculate late fees for an overdue installment
 */
export const calculateLateFee = (
    amountDue: number,
    dueDate: Date,
    currentDate: Date = new Date(),
    config: Partial<LateFeeConfig> = {}
): LateFeeResult => {
    const settings = { ...DEFAULT_LATE_FEE_CONFIG, ...config };

    const dueDateMs = dueDate.getTime();
    const currentMs = currentDate.getTime();
    const daysDiff = Math.floor((currentMs - dueDateMs) / (1000 * 60 * 60 * 24));

    // Check if within grace period
    if (daysDiff <= settings.gracePeriodDays) {
        return {
            isLate: false,
            daysLate: 0,
            flatFee: 0,
            percentageFee: 0,
            totalFee: 0,
            totalAmountDue: amountDue,
            currency: settings.currency,
            breakdown: {
                originalAmount: amountDue,
                lateFee: 0,
                interestAccrued: 0,
            },
        };
    }

    const daysLate = daysDiff - settings.gracePeriodDays;

    // Calculate percentage fee
    let percentageFee: number;
    if (settings.compoundDaily) {
        // Compound daily: A * (1 + r)^n - A
        percentageFee = amountDue * (Math.pow(1 + settings.percentageRate / 100, daysLate) - 1);
    } else {
        // Simple daily: A * r * n
        percentageFee = amountDue * (settings.percentageRate / 100) * daysLate;
    }

    // Apply maximum cap
    const maxFee = amountDue * (settings.maxPercentageFee / 100);
    percentageFee = Math.min(percentageFee, maxFee);

    const totalFee = settings.flatFee + percentageFee;

    return {
        isLate: true,
        daysLate,
        flatFee: Math.round(settings.flatFee * 100) / 100,
        percentageFee: Math.round(percentageFee * 100) / 100,
        totalFee: Math.round(totalFee * 100) / 100,
        totalAmountDue: Math.round((amountDue + totalFee) * 100) / 100,
        currency: settings.currency,
        breakdown: {
            originalAmount: amountDue,
            lateFee: Math.round(totalFee * 100) / 100,
            interestAccrued: Math.round(percentageFee * 100) / 100,
        },
    };
};

/**
 * Calculate monthly payment using amortization formula
 */
export const calculateMonthlyPayment = (
    principal: number,
    annualInterestRate: number,
    termMonths: number
): number => {
    if (annualInterestRate === 0) {
        return principal / termMonths;
    }

    const monthlyRate = annualInterestRate / 100 / 12;
    const payment =
        (principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) /
        (Math.pow(1 + monthlyRate, termMonths) - 1);

    return Math.round(payment * 100) / 100;
};

/**
 * Generate full amortization schedule
 */
export const generateAmortizationSchedule = (
    principal: number,
    annualInterestRate: number,
    termMonths: number,
    startDate: Date = new Date(),
    currency: string = "ZMW"
): LoanSummary => {
    const monthlyPayment = calculateMonthlyPayment(principal, annualInterestRate, termMonths);
    const monthlyRate = annualInterestRate / 100 / 12;

    const installments: InstallmentInfo[] = [];
    let remainingBalance = principal;
    let totalInterest = 0;

    for (let i = 1; i <= termMonths; i++) {
        const interestDue = remainingBalance * monthlyRate;
        const principalDue = monthlyPayment - interestDue;
        remainingBalance -= principalDue;
        totalInterest += interestDue;

        const targetYear = startDate.getFullYear() + Math.floor((startDate.getMonth() + i) / 12);
        const targetMonth = (startDate.getMonth() + i) % 12;
        const lastDayOfMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
        const dueDate = new Date(targetYear, targetMonth, Math.min(startDate.getDate(), lastDayOfMonth));

        installments.push({
            installmentNumber: i,
            dueDate,
            principalDue: Math.round(principalDue * 100) / 100,
            interestDue: Math.round(interestDue * 100) / 100,
            totalDue: Math.round(monthlyPayment * 100) / 100,
            amountPaid: 0,
            status: "pending",
            lateFee: 0,
            remainingBalance: Math.max(0, Math.round(remainingBalance * 100) / 100),
        });
    }

    return {
        principal,
        totalInterest: Math.round(totalInterest * 100) / 100,
        totalAmount: Math.round((principal + totalInterest) * 100) / 100,
        monthlyPayment,
        effectiveApr: annualInterestRate,
        currency,
        termMonths,
        installments,
    };
};

/**
 * Calculate early payoff amount with discount
 */
export const calculateEarlyPayoff = (
    remainingBalance: number,
    remainingMonths: number,
    earlyPayoffDiscountRate: number = 2 // 2% discount for early payoff
): {
    originalRemaining: number;
    discount: number;
    earlyPayoffAmount: number;
    savings: number;
} => {
    const discount = remainingBalance * (earlyPayoffDiscountRate / 100);
    const earlyPayoffAmount = remainingBalance - discount;

    return {
        originalRemaining: Math.round(remainingBalance * 100) / 100,
        discount: Math.round(discount * 100) / 100,
        earlyPayoffAmount: Math.round(earlyPayoffAmount * 100) / 100,
        savings: Math.round(discount * 100) / 100,
    };
};

/**
 * Calculate early settlement amount with interest savings
 */
export const calculateEarlySettlement = (
    outstandingBalance: number,
    annualInterestRate: number,
    remainingMonths: number,
    settlementDiscountPercent: number = 50 // 50% discount on remaining interest
): {
    outstandingPrincipal: number;
    remainingInterest: number;
    interestSaved: number;
    settlementAmount: number;
    discountPercent: number;
} => {
    // Estimate remaining interest (simplified)
    const monthlyRate = annualInterestRate / 100 / 12;
    const remainingInterest = outstandingBalance * monthlyRate * remainingMonths * 0.5; // Approximation

    // Apply discount to interest portion
    const interestSaved = remainingInterest * (settlementDiscountPercent / 100);
    const settlementAmount = outstandingBalance - interestSaved;

    return {
        outstandingPrincipal: Math.round(outstandingBalance * 100) / 100,
        remainingInterest: Math.round(remainingInterest * 100) / 100,
        interestSaved: Math.round(interestSaved * 100) / 100,
        settlementAmount: Math.round(settlementAmount * 100) / 100,
        discountPercent: settlementDiscountPercent
    };
};

/**
 * Calculate total fees for multiple overdue installments
 */
export const calculateTotalLateFees = (
    installments: Array<{
        amountDue: number;
        dueDate: Date;
        status: string;
    }>,
    currentDate: Date = new Date(),
    config: Partial<LateFeeConfig> = {}
): {
    totalLateFees: number;
    lateInstallments: number;
    details: LateFeeResult[];
} => {
    const details: LateFeeResult[] = [];
    let totalLateFees = 0;
    let lateInstallments = 0;

    for (const installment of installments) {
        if (installment.status !== "paid") {
            const result = calculateLateFee(
                installment.amountDue,
                installment.dueDate,
                currentDate,
                config
            );

            details.push(result);

            if (result.isLate) {
                totalLateFees += result.totalFee;
                lateInstallments++;
            }
        }
    }

    return {
        totalLateFees: Math.round(totalLateFees * 100) / 100,
        lateInstallments,
        details,
    };
};

/**
 * Determine loan status based on payment history
 */
export const determineLoanStatus = (
    installments: InstallmentInfo[],
    currentDate: Date = new Date()
): {
    status: "current" | "late" | "severely_delinquent" | "default" | "paid_off";
    daysDelinquent: number;
    missedPayments: number;
} => {
    const pendingInstallments = installments.filter(
        (i) => i.status !== "paid" && new Date(i.dueDate) < currentDate
    );

    if (pendingInstallments.length === 0) {
        const allPaid = installments.every((i) => i.status === "paid");
        return {
            status: allPaid ? "paid_off" : "current",
            daysDelinquent: 0,
            missedPayments: 0,
        };
    }

    const oldestUnpaid = pendingInstallments.reduce((oldest, current) =>
        new Date(current.dueDate) < new Date(oldest.dueDate) ? current : oldest
    );

    const daysDelinquent = Math.floor(
        (currentDate.getTime() - new Date(oldestUnpaid.dueDate).getTime()) /
        (1000 * 60 * 60 * 24)
    );

    let status: "current" | "late" | "severely_delinquent" | "default";

    if (daysDelinquent <= 30) {
        status = "late";
    } else if (daysDelinquent <= 90) {
        status = "severely_delinquent";
    } else {
        status = "default";
    }

    return {
        status,
        daysDelinquent,
        missedPayments: pendingInstallments.length,
    };
};

/**
 * Calculate credit score impact based on payment behavior
 */
export const calculateCreditScoreImpact = (
    currentScore: number,
    onTimePayments: number,
    latePayments: number,
    missedPayments: number
): {
    newScore: number;
    change: number;
    factors: Array<{ factor: string; impact: number }>;
} => {
    const factors: Array<{ factor: string; impact: number }> = [];
    let scoreChange = 0;

    // On-time payments boost score
    const onTimeBoost = Math.min(onTimePayments * 5, 50);
    if (onTimeBoost > 0) {
        factors.push({ factor: "On-time payments", impact: onTimeBoost });
        scoreChange += onTimeBoost;
    }

    // Late payments reduce score
    const latePenalty = latePayments * -15;
    if (latePenalty < 0) {
        factors.push({ factor: "Late payments", impact: latePenalty });
        scoreChange += latePenalty;
    }

    // Missed payments severely reduce score
    const missedPenalty = missedPayments * -35;
    if (missedPenalty < 0) {
        factors.push({ factor: "Missed payments", impact: missedPenalty });
        scoreChange += missedPenalty;
    }

    const newScore = Math.max(300, Math.min(850, currentScore + scoreChange));

    return {
        newScore,
        change: scoreChange,
        factors,
    };
};

/**
 * Format currency amount for display
 */
export const formatCurrency = (
    amount: number,
    currency: string = "ZMW"
): string => {
    const formatter = new Intl.NumberFormat("en-ZM", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    // Handle ZMW specifically since it may not be recognized
    try {
        return formatter.format(amount);
    } catch {
        return `K${amount.toLocaleString("en-ZM", { minimumFractionDigits: 2 })}`;
    }
};

/**
 * Get human-readable status description
 */
export const getStatusDescription = (
    status: string
): { label: string; color: string; severity: "success" | "warning" | "error" | "info" } => {
    const statusMap: Record<string, { label: string; color: string; severity: "success" | "warning" | "error" | "info" }> = {
        current: { label: "Current", color: "green", severity: "success" },
        paid: { label: "Paid", color: "green", severity: "success" },
        paid_off: { label: "Paid Off", color: "green", severity: "success" },
        pending: { label: "Pending", color: "blue", severity: "info" },
        partial: { label: "Partially Paid", color: "yellow", severity: "warning" },
        late: { label: "Late", color: "orange", severity: "warning" },
        overdue: { label: "Overdue", color: "red", severity: "error" },
        severely_delinquent: { label: "Severely Delinquent", color: "red", severity: "error" },
        default: { label: "In Default", color: "red", severity: "error" },
        active: { label: "Active", color: "blue", severity: "info" },
        approved: { label: "Approved", color: "green", severity: "success" },
        rejected: { label: "Rejected", color: "red", severity: "error" },
        disbursed: { label: "Disbursed", color: "blue", severity: "info" },
    };

    return statusMap[status.toLowerCase()] || { label: status, color: "gray", severity: "info" };
};
